import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { CustomMessageService } from '../custom-message.service';
import { CalculationService } from '../shared/shared-services/calcuation.service';
import { CalculationType } from '../backend-response-models/calc-enums.helper';
import { CalculationResults, SingleMyopeResults } from '../backend-response-models/calc-interfaces.helper';
import { Language } from '../shared/shared-interfaces/shared.interfaces';
import { ReportType } from '../shared/shared-enums/shared.enums';
import { PDFService } from '../shared/shared-services/pdf.service';
import { Location } from '@angular/common';

@Injectable({
  providedIn: null,
})
export class DashboardCubit {
  private calcResults?: CalculationResults;
  private reportType: ReportType = ReportType.ECP;

  private uiStateSubject: BehaviorSubject<DashboardState> = new BehaviorSubject<DashboardState>(new Loading());
  public uiStateChanged: Observable<DashboardState> = this.uiStateSubject.asObservable();

  constructor(
    private readonly customMessageService: CustomMessageService,
    private readonly calculationService: CalculationService,
    private readonly pdfService: PDFService
  ) {}

  async begin(token: string | null, id: string | null, useInternalApi: boolean, startingReportType?: ReportType): Promise<void> {
    try {
      if (startingReportType) {
        this.reportType = startingReportType;
      }

      this.uiStateSubject.next(new Loading());

      if (!id || !token) {
        throw new Error('Invalid ID or token');
      }

      this.calcResults = await this.calculationService.getCalculationWithTokenAndId(token, id, useInternalApi);

      await new Promise(resolve => setTimeout(resolve, 500));
      if (!this.calcResults) {
        throw new Error('Calculation results not found');
      }

      console.log('Calculation results returned with type:', this.calcResults.calculationType);

      if (this.calcResults.calculationType === CalculationType.MYOPES_ONE_VISIT) {
        this.uiStateSubject.next(new SingleMyopeDisplay(this.calcResults, this.reportType));
        return;
      }

      if (this.calcResults.calculationType === CalculationType.MYOPES_MULTI_VISITS) {
        this.uiStateSubject.next(new MyopiaProgressionDisplay(this.calcResults, this.reportType));
        return;
      }

      if (this.calcResults.calculationType === CalculationType.PREMYOPES) {
        this.uiStateSubject.next(new PremyopeDisplay(this.calcResults, this.reportType));
        return;
      }

      if (this.calcResults.calculationType === CalculationType.NO_CALCULATION_PLOTS_ONLY) {
        this.uiStateSubject.next(new NoCalcDisplay(this.calcResults, this.reportType));
        return;
      }
    } catch (error) {
      this.handleError(error);
    }
  }

  async prepareDashboardStates(): Promise<void> {
    if (!this.calcResults) {
      throw new Error('Calculation results not found');
    }

    if (this.calcResults.calculationType === CalculationType.MYOPES_ONE_VISIT) {
      this.uiStateSubject.next(new SingleMyopeDisplay(this.calcResults, this.reportType));
      return;
    }

    if (this.calcResults.calculationType === CalculationType.MYOPES_MULTI_VISITS) {
      this.uiStateSubject.next(new MyopiaProgressionDisplay(this.calcResults, this.reportType));
      return;
    }

    if (this.calcResults.calculationType === CalculationType.PREMYOPES) {
      this.uiStateSubject.next(new PremyopeDisplay(this.calcResults, this.reportType));
      return;
    }

    if (this.calcResults.calculationType === CalculationType.NO_CALCULATION_PLOTS_ONLY) {
      this.uiStateSubject.next(new NoCalcDisplay(this.calcResults, this.reportType));
      return;
    }
  }

  async reportTypeDidChange(reportType: ReportType): Promise<void> {
    try {
      if (!this.calcResults) {
        return;
      }

      this.uiStateSubject.next(new Loading());
      this.reportType = reportType;
      await new Promise(resolve => setTimeout(resolve, 500));
      await this.prepareDashboardStates();
    } catch (error) {
      this.handleError(error);
    }
  }

  async downloadPressed(): Promise<void> {
    const currentState = this.uiStateSubject.getValue();

    try {
      this.uiStateSubject.next(new Loading());
      if (!this.calcResults) {
        throw new Error('Calculation results not found');
      }

      //Get current url
      const fullUrl = window.location.href; // Gets complete URL
      //add some params to the url
      const url = new URL(fullUrl);
      url.searchParams.append('hide-menu', 'true');
      url.searchParams.append('report-type', this.reportType === ReportType.Parent ? 'parent' : 'ecp');
      url.searchParams.append('use-internal-api', 'true');
      await this.pdfService.getPdfGeneration(url.toString());
    } catch (error) {
      this.handleError(error);
    } finally {
      this.uiStateSubject.next(currentState);
    }
  }

  private handleError(error: unknown) {
    console.error(error);
    const errorDescription = JSON.stringify(error, Object.getOwnPropertyNames(error));
    this.uiStateSubject.next(new ErrorRetry(errorDescription));
    this.customMessageService.showErrorMessage(errorDescription);
  }

  async languageDidChange(language: Language): Promise<void> {
    //Causes a refresh of page components with all language changes
    const savedState = this.uiStateSubject.getValue();
    this.uiStateSubject.next(new Loading());
    await new Promise(resolve => setTimeout(resolve, 500));
    this.uiStateSubject.next(savedState);
  }
}

export interface DashboardState {
  stateType: DashboardStateType;
  reportType?: ReportType | null;
  hideMenu?: boolean;
}

export class Loading implements DashboardState {
  stateType: DashboardStateType = DashboardStateType.LOADING;
  reportType?: ReportType | null;
}

export class SingleMyopeDisplay implements DashboardState {
  stateType: DashboardStateType = DashboardStateType.SINGLE_MYOPES;
  constructor(
    public readonly calcResults: CalculationResults,
    public reportType: ReportType
  ) {}
}

export class MyopiaProgressionDisplay implements DashboardState {
  stateType: DashboardStateType = DashboardStateType.MYOPES_PROGRESSION;
  constructor(
    public readonly calcResults: CalculationResults,
    public reportType: ReportType
  ) {}
}

export class PremyopeDisplay implements DashboardState {
  stateType: DashboardStateType = DashboardStateType.PREMYOPES;
  constructor(
    public readonly calcResults: CalculationResults,
    public reportType: ReportType
  ) {}
}

export class NoCalcDisplay implements DashboardState {
  stateType: DashboardStateType = DashboardStateType.NO_CALC;
  constructor(
    public readonly calcResults: CalculationResults,
    public reportType: ReportType
  ) {}
}

export class ErrorRetry implements DashboardState {
  stateType: DashboardStateType = DashboardStateType.ERROR_RETRY;
  constructor(public readonly errorDescripton: string) {}
}

export enum DashboardStateType {
  LOADING = 'LOADING',
  SINGLE_MYOPES = 'SINGLE_MYOPES',
  MYOPES_PROGRESSION = 'MYOPES_PROGRESSION',
  PREMYOPES = 'PREMYOPES',
  NO_CALC = 'NO_CALC',
  ERROR_RETRY = 'ERROR_RETRY',
}
